import { distinct, intersect } from "@entrilia/shared/utilities/arrayHelper";
import type { DescriptiveDateRange } from "@entrilia/shared/utilities/dateFilterHelper";
import { last12Months } from "@entrilia/shared/utilities/dateFilterHelper";
import type {
  DocumentCollection,
  DocumentCollectionPortalPublishStatus,
  DocumentCollectionPublishStatus,
} from "../../../api/types/documentCollectionTypes";
import storage from "../../../storage/storage";
import { createSearchFilter } from "./searchFilter";

export interface DocumentCollectionsFilterState {
  allItems: DocumentCollection[];
  filteredItems: DocumentCollection[];
  searchTerm: string;
  dateRange: DescriptiveDateRange;
  allFunds: string[];
  selectedFunds: string[];
  selectedStatuses: DocumentCollectionPublishStatus[];
  selectedPortalStatuses: DocumentCollectionPortalPublishStatus[];
}

type Action =
  | {
      type: "LOAD_DATA";
      items: DocumentCollection[];
    }
  | {
      type: "SEARCH";
      searchTerm: string;
    }
  | {
      type: "FILTER_FUNDS";
      selectedFunds: string[];
    }
  | {
      type: "FILTER_STATUSES";
      selectedStatuses: DocumentCollectionPublishStatus[];
    }
  | {
      type: "FILTER_PORTAL_STATUSES";
      selectedPortalStatuses: DocumentCollectionPortalPublishStatus[];
    }
  | {
      type: "SET_DATE_RANGE";
      dateRange: DescriptiveDateRange;
      clientCode: string;
    }
  | {
      type: "CLEAR_FILTERS";
      clientCode: string;
    };

export const statusFilterOptions: { value: DocumentCollectionPublishStatus; label: string }[] = [
  {
    value: "Preview",
    label: "Preview",
  },
  {
    value: "PartiallyLive",
    label: "Live (Partially)",
  },
  {
    value: "Live",
    label: "Live",
  },
];

export const portalStatusFilterOptions: { value: DocumentCollectionPortalPublishStatus; label: string }[] = [
  {
    value: "AllPublished",
    label: "Published",
  },
  {
    value: "PartiallyPublished",
    label: "Partially Published",
  },
  {
    value: "NonePublished",
    label: "Not Published",
  },
];

const applySearch = createSearchFilter<DocumentCollection>(1, (c) => [
  c.name,
  ...c.funds.map((f) => f.name),
  ...c.categories,
  c.no?.toLocaleString() ?? "",
]);

const itemMatchesFilters = (item: DocumentCollection, state: DocumentCollectionsFilterState): boolean => {
  const matchesFund =
    state.selectedFunds.length === 0 ||
    intersect(
      state.selectedFunds,
      item.funds.map((f) => f.name)
    ).length > 0;

  const matchesStatus = state.selectedStatuses.length === 0 || state.selectedStatuses.includes(item.status);

  const matchesPortalStatus =
    state.selectedPortalStatuses.length === 0 || state.selectedPortalStatuses.includes(item.portalPublishStatus);

  return matchesFund && matchesStatus && matchesPortalStatus;
};

const applyFilters = (newState: DocumentCollectionsFilterState): DocumentCollectionsFilterState => {
  let filteredItems = newState.allItems;
  filteredItems = filteredItems.filter((item) => itemMatchesFilters(item, newState));
  filteredItems = applySearch(filteredItems, newState.searchTerm);
  return { ...newState, filteredItems };
};

const getAllFundNames = (items: DocumentCollection[]) => {
  const fundNames = items.flatMap((item) => item.funds.map((f) => f.name)).filter(Boolean);
  return distinct(fundNames).sort((a, b) => a.localeCompare(b));
};

export const defaultDateRange = last12Months;

export const getInitialState = (clientCode: string): DocumentCollectionsFilterState => ({
  allItems: [],
  filteredItems: [],
  searchTerm: "",
  allFunds: [],
  selectedFunds: [],
  selectedStatuses: [],
  selectedPortalStatuses: [],
  dateRange: storage.getDocumentCollectionsDateRange(clientCode) ?? defaultDateRange,
});

export const reducer = (state: DocumentCollectionsFilterState, action: Action): DocumentCollectionsFilterState => {
  switch (action.type) {
    case "LOAD_DATA": {
      return applyFilters({
        ...state,
        allItems: action.items,
        allFunds: getAllFundNames(action.items),
      });
    }
    case "SEARCH": {
      return applyFilters({ ...state, searchTerm: action.searchTerm });
    }
    case "FILTER_FUNDS": {
      return applyFilters({ ...state, selectedFunds: action.selectedFunds });
    }
    case "FILTER_STATUSES": {
      return applyFilters({ ...state, selectedStatuses: action.selectedStatuses });
    }
    case "FILTER_PORTAL_STATUSES": {
      return applyFilters({ ...state, selectedPortalStatuses: action.selectedPortalStatuses });
    }
    case "SET_DATE_RANGE": {
      storage.saveDocumentCollectionsDateRange(action.clientCode, action.dateRange);
      return { ...state, dateRange: action.dateRange };
    }
    case "CLEAR_FILTERS": {
      storage.saveDocumentCollectionsDateRange(action.clientCode, defaultDateRange);
      return {
        ...state,
        dateRange: defaultDateRange,
        selectedFunds: [],
        selectedStatuses: [],
        selectedPortalStatuses: [],
        filteredItems: applySearch(state.allItems, state.searchTerm),
      };
    }
    default: {
      return state;
    }
  }
};
